import { Quote, Star } from "lucide-react";
import { Container } from "../layout/Container.jsx";

/**
 * Continuously sliding testimonials marquee. Two rows scrolling in opposite
 * directions with side-fade masks for that polished aesthetic. Pauses on
 * hover so visitors can actually read.
 */
export function TestimonialsCarousel({ testimonials = [] }) {
  if (testimonials.length === 0) return null;

  // Duplicate the list once so the CSS marquee loop can be seamless.
  const loop = [...testimonials, ...testimonials];
  // Even and odd index split for a two-row staggered effect when we have
  // enough items; otherwise everything shares one row.
  const useSplit = testimonials.length >= 4;
  const rowA = useSplit ? testimonials.filter((_, i) => i % 2 === 0) : testimonials;
  const rowB = useSplit ? testimonials.filter((_, i) => i % 2 === 1) : null;

  return (
    <section className="border-t border-border-subtle bg-bg-base py-20 lg:py-28">
      <Container>
        <div className="mb-12 max-w-2xl">
          <p className="eyebrow">What customers say</p>
          <h2 className="mt-3 font-display text-4xl text-text-primary lg:text-5xl">
            Built well, recommended often
          </h2>
          <p className="mt-4 text-text-secondary">
            Real reviews from homeowners across Melbourne and regional Victoria.
          </p>
        </div>
      </Container>

      {/* Marquee track — uses CSS keyframes defined in styles.css. The
          masked side-fade is achieved with a horizontal mask gradient.
          overflow-hidden prevents the wide row from triggering page-level
          horizontal scrollbars. */}
      <div
        className="group relative overflow-hidden"
        style={{
          maskImage:
            "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
        }}
      >
        <MarqueeRow items={useSplit ? [...rowA, ...rowA] : loop} duration={42} />
        {rowB && (
          <div className="mt-6">
            <MarqueeRow items={[...rowB, ...rowB]} duration={56} reverse />
          </div>
        )}
      </div>
    </section>
  );
}

function MarqueeRow({ items, duration = 40, reverse = false }) {
  return (
    <div className="flex w-max gap-6 px-3 py-2 group-hover:[animation-play-state:paused] motion-reduce:[animation-name:none]"
      style={{
        animation: `marquee ${duration}s linear infinite ${reverse ? "reverse" : ""}`.trim(),
      }}
    >
      {items.map((t, i) => (
        <TestimonialCard key={`${t._id ?? t.customerName}-${i}`} t={t} />
      ))}
    </div>
  );
}

function TestimonialCard({ t }) {
  const initials = (t.customerName ?? "·")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("");
  return (
    <article className="w-[340px] shrink-0 rounded-2xl border border-border-subtle bg-bg-elevated p-6 transition-colors hover:border-accent/60 lg:w-[420px] lg:p-7">
      <Quote className="mb-4 h-7 w-7 text-accent/40" />
      <blockquote className="text-base leading-relaxed text-text-primary lg:text-lg">
        &ldquo;{t.text}&rdquo;
      </blockquote>
      <div className="mt-6 flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-accent/15 text-sm font-semibold text-accent">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-text-primary">{t.customerName}</p>
          <div className="mt-0.5 flex items-center gap-2">
            <StarRating value={t.rating} />
            {t.source && (
              <span className="truncate text-xs text-text-muted">via {t.source}</span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function StarRating({ value = 5 }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i < value ? "fill-accent text-accent" : "text-text-muted"}`}
        />
      ))}
    </div>
  );
}